/**
 * Featured Collection Custom - Carrossel com Transform
 * Baseado no Banner Grid 2 Carousel
 * Usa CSS transform ao invés de scroll nativo para permitir overflow:visible
 * Suporta responsivo com diferentes números de cards por viewport
 */

(function () {
    'use strict';

    const CONFIG = {
        duration: 500,
        gap: 15,
        padding: 15,
        breakpoints: {
            mobile: 480,
            tablet: 768
        }
    };

    class FeaturedCollectionCarousel {
        constructor(section) {
            this.section = section;
            this.viewport = section.querySelector('.featured-collection-custom__viewport');
            this.track = section.querySelector('.gridy-track');
            this.slider = section.querySelector('gridy-slider');

            if (!this.track || !this.viewport || !this.slider) return;

            // Ler configuração do tema via data-attributes
            this.iprDesktop = parseInt(this.slider.getAttribute('data-ipr-desktop') || '4', 10);
            this.iprTablet = parseInt(this.slider.getAttribute('data-ipr-tablet') || '2', 10);
            this.iprMobile = parseInt(this.slider.getAttribute('data-ipr-mobile') || '1', 10);

            // Pega os cards de produto (não os clones)
            this.items = Array.from(this.track.querySelectorAll('.card--product:not(.carousel-clone)'));
            this.totalItems = this.items.length;

            // Verifica se precisa de carrossel
            this.maxVisible = this.getCurrentMaxVisible();

            if (this.totalItems <= this.maxVisible) {
                this.track.style.justifyContent = 'center';
                const arrows = section.querySelector('.gridy-arrows');
                if (arrows) arrows.style.display = 'none';
                return;
            }

            this.currentIndex = 0;
            this.isAnimating = false;
            this.init();
        }

        getCurrentMaxVisible() {
            const width = window.innerWidth;
            if (width <= CONFIG.breakpoints.mobile) {
                return this.iprMobile;
            } else if (width <= CONFIG.breakpoints.tablet) {
                return this.iprTablet;
            }
            return this.iprDesktop;
        }

        init() {
            // 1. Limpar clones antigos (se houver)
            this.track.querySelectorAll('.carousel-clone').forEach(el => el.remove());

            // 2. Criar Clones para loop infinito
            this.createClones();

            // 3. Configurar Eventos dos Botões
            this.setupArrows();

            // 4. Posicionamento Inicial (sem animação)
            requestAnimationFrame(() => {
                this.updatePosition(false);
            });

            // 5. Resize Listener - recalcula tudo
            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    this.maxVisible = this.getCurrentMaxVisible();
                    this.updatePosition(false);
                }, 100);
            });

            // 6. Setup Mouse Drag for Desktop
            this.setupDragScroll();

            this.section.setAttribute('data-carousel-init', 'true');
        }

        setupDragScroll() {
            // IMPORTANT: do not add `.grabbing` (pointer-events:none on children)
            // until the pointer has actually moved. Adding it on mousedown kills
            // product link clicks (mousedown → pe:none → click never fires).
            let isDown = false;
            let isDragging = false;
            let startX = 0;
            const swipeThreshold = 50;
            const dragThreshold = 8;

            const endDrag = () => {
                isDown = false;
                isDragging = false;
                this.viewport.classList.remove('grabbing');
            };

            this.viewport.addEventListener('mousedown', (e) => {
                if (e.button !== 0) return;
                isDown = true;
                isDragging = false;
                startX = e.pageX - this.viewport.offsetLeft;
            });

            this.viewport.addEventListener('mouseleave', () => {
                endDrag();
            });

            this.viewport.addEventListener('mouseup', (e) => {
                if (!isDown) return;
                const x = e.pageX - this.viewport.offsetLeft;
                const walk = startX - x;
                const wasDragging = isDragging;
                endDrag();

                if (!wasDragging) return; // pure click — let <a> navigate

                if (walk > swipeThreshold) {
                    this.next();
                } else if (walk < -swipeThreshold) {
                    this.prev();
                }

                // Suppress the click that follows a drag so links don't navigate mid-swipe
                const suppress = (ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    this.viewport.removeEventListener('click', suppress, true);
                };
                this.viewport.addEventListener('click', suppress, true);
            });

            this.viewport.addEventListener('mousemove', (e) => {
                if (!isDown) return;
                const x = e.pageX - this.viewport.offsetLeft;
                if (!isDragging && Math.abs(startX - x) > dragThreshold) {
                    isDragging = true;
                    this.viewport.classList.add('grabbing');
                }
                if (isDragging) e.preventDefault();
            });

            this.viewport.style.cursor = 'grab';
        }

        createClones() {
            // Clonar baseado no máximo visível em desktop (para cobrir todos os casos)
            const clonesCount = Math.max(this.iprDesktop, this.iprTablet, this.iprMobile);
            const clonesBefore = this.items.slice(-clonesCount).map(item => item.cloneNode(true));
            const clonesAfter = this.items.slice(0, clonesCount).map(item => item.cloneNode(true));

            clonesBefore.reverse().forEach(clone => {
                clone.classList.add('carousel-clone');
                this.track.insertBefore(clone, this.track.firstChild);
            });

            clonesAfter.forEach(clone => {
                clone.classList.add('carousel-clone');
                this.track.appendChild(clone);
            });

            this.clonesOffset = clonesCount;
        }

        getItemWidth() {
            // Pega a largura REAL do item do DOM
            const item = this.track.querySelector('.card--product:not(.carousel-clone)');
            if (!item) return 0;

            const rect = item.getBoundingClientRect();
            return rect.width + CONFIG.gap;
        }

        updatePosition(animate = true) {
            const step = this.getItemWidth();
            if (step === 0) return;

            // Offset considera: clones * step
            const offset = (this.clonesOffset + this.currentIndex) * step;

            if (!animate) {
                this.track.style.transition = 'none';
            } else {
                this.track.style.transition = `transform ${CONFIG.duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
            }

            this.track.style.transform = `translateX(-${offset}px)`;

            if (!animate) {
                void this.track.offsetHeight; // force reflow
            }
        }

        next() {
            if (this.isAnimating) return;
            this.isAnimating = true;

            this.currentIndex++;
            this.updatePosition(true);

            setTimeout(() => {
                if (this.currentIndex >= this.totalItems) {
                    this.currentIndex = 0;
                    this.updatePosition(false);
                }
                this.isAnimating = false;
            }, CONFIG.duration);
        }

        prev() {
            if (this.isAnimating) return;
            this.isAnimating = true;

            this.currentIndex--;
            this.updatePosition(true);

            setTimeout(() => {
                if (this.currentIndex < 0) {
                    this.currentIndex = this.totalItems - 1;
                    this.updatePosition(false);
                }
                this.isAnimating = false;
            }, CONFIG.duration);
        }

        setupArrows() {
            const prev = this.section.querySelector('[data-direction="prev"]');
            const next = this.section.querySelector('[data-direction="next"]');

            if (prev) {
                const newPrev = prev.cloneNode(true);
                prev.parentNode.replaceChild(newPrev, prev);
                newPrev.addEventListener('click', (e) => { e.preventDefault(); this.prev(); });
            }
            if (next) {
                const newNext = next.cloneNode(true);
                next.parentNode.replaceChild(newNext, next);
                newNext.addEventListener('click', (e) => { e.preventDefault(); this.next(); });
            }
        }
    }

    function init() {
        document.querySelectorAll('.featured-collection-custom:not([data-carousel-init])').forEach(s => {
            new FeaturedCollectionCarousel(s);
        });
    }

    // Esperar o CSS carregar e aplicar as larguras
    window.addEventListener('load', init);
    document.addEventListener('shopify:section:load', () => setTimeout(init, 200));

    // Quick init for hot reload
    if (document.readyState === 'complete') init();
})();
